import type { Ingredient, Recipe } from './types';
import type { IngredientInput } from './validation';
import type { Unit } from './units';
import { dimensionOf } from './units';
import { normalizeText } from './importer';
import { parseUnit } from './importCatalog';

const STOP_WORDS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'fresco', 'fresca', 'frescos', 'frescas']);

export function normalizeIngredientName(value: string): string {
  const words = normalizeText(value)
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => !STOP_WORDS.has(word))
    .map((word) => {
      if (word.length <= 4) return word;
      if (word.endsWith('s') && !word.endsWith('us') && !word.endsWith('is')) return word.slice(0, -1);
      return word;
    });
  return words.join(' ');
}

function bigrams(value: string): Set<string> {
  const compact = value.replace(/\s+/g, ' ');
  if (compact.length < 2) return new Set([compact]);
  return new Set(Array.from({ length: compact.length - 1 }, (_, i) => compact.slice(i, i + 2)));
}

export function diceSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const aa = bigrams(a);
  const bb = bigrams(b);
  let overlap = 0;
  for (const pair of aa) if (bb.has(pair)) overlap += 1;
  return (2 * overlap) / (aa.size + bb.size);
}

export interface IngredientCandidate {
  ingredientId: string;
  name: string;
  score: number;
}

export type RecipeProposalCatalogItem = Pick<Ingredient, 'ingredientId' | 'name' | 'purchaseUnit'>;

export type IngredientMatch =
  | { kind: 'exact' | 'fuzzy'; candidates: [IngredientCandidate] }
  | { kind: 'ambiguous'; candidates: IngredientCandidate[] }
  | { kind: 'none'; candidates: [] };

export function matchIngredient(
  name: string,
  catalog: RecipeProposalCatalogItem[],
): IngredientMatch {
  const target = normalizeIngredientName(name);
  const exact = catalog.filter((item) => normalizeIngredientName(item.name) === target);
  if (exact.length === 1) {
    return { kind: 'exact', candidates: [{ ...exact[0], score: 1 }] };
  }
  if (exact.length > 1) {
    return { kind: 'ambiguous', candidates: exact.map((item) => ({ ...item, score: 1 })) };
  }
  const contained = catalog.filter((item) => {
    const normalized = normalizeIngredientName(item.name);
    return normalized.startsWith(`${target} `) || normalized.split(' ').includes(target);
  });
  if (contained.length > 1) {
    return {
      kind: 'ambiguous',
      candidates: contained.map((item) => ({ ...item, score: 0.8 })),
    };
  }
  // Los nombres muy cortos solo se enlazan de forma exacta: "sal" no es "salmón".
  if (target.length < 5) return { kind: 'none', candidates: [] };

  const ranked = catalog
    .map((item) => ({ ...item, score: diceSimilarity(target, normalizeIngredientName(item.name)) }))
    .filter((item) => item.score >= 0.7)
    .sort((a, b) => b.score - a.score);
  if (ranked.length === 0) return { kind: 'none', candidates: [] };
  const [first, second] = ranked;
  // Una errata clara se puede enlazar sola. Por debajo de este umbral es más
  // seguro preguntar: una coincidencia falsa altera el coste sin que se vea.
  if (first.score >= 0.9 && (!second || first.score - second.score >= 0.1)) {
    return { kind: 'fuzzy', candidates: [first] };
  }
  return { kind: 'ambiguous', candidates: ranked.slice(0, 5) };
}

const NUMBER_WORDS: Record<string, number> = {
  un: 1, una: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
  seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10,
};

export interface ParsedQuantity {
  qty?: number;
  unit?: Unit;
  estimated: boolean;
  issue?: string;
}

export function parseQuantity(text: string, ingredientName?: string): ParsedQuantity {
  const clean = normalizeText(text).replace(',', '.');
  if (/\b(pizca|al gusto)\b/.test(clean)) {
    return { estimated: true, issue: 'Cantidad no medible; revísala antes de guardar.' };
  }
  const fraction = /\b(un cuarto|una cuarta parte) de (kilo|kilogramo|litro)\b/.exec(clean);
  if (fraction) return { qty: 0.25, unit: fraction[2].startsWith('l') ? 'l' : 'kg', estimated: false };
  const half = /\b(medio|media) (kilo|kilogramo|litro)\b/.exec(clean);
  if (half) return { qty: 0.5, unit: half[2].startsWith('l') ? 'l' : 'kg', estimated: false };
  const spoon = /\b(\d+(?:\.\d+)?|una?|dos)?\s*cucharadas?\b/.exec(clean);
  if (spoon) {
    const count = Number(spoon[1]) || NUMBER_WORDS[spoon[1] ?? ''] || 1;
    return { qty: count * 15, unit: 'ml', estimated: true };
  }
  const countNoun = /\b(\d+(?:\.\d+)?|un|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\s+(?:dientes?|piezas?|unidades?|uds?)\b/.exec(clean);
  if (countNoun) return { qty: Number(countNoun[1]) || NUMBER_WORDS[countNoun[1]], unit: 'pz', estimated: false };
  const numeric = /\b(\d+(?:\.\d+)?)\s*(kg|kilos?|kilogramos?|g|gr|gramos?|l|litros?|ml|mililitros?|cl|piezas?|unidades?|uds?)\b/.exec(clean);
  if (numeric) return { qty: Number(numeric[1]), unit: parseUnit(numeric[2]), estimated: false };
  if (ingredientName) {
    const name = normalizeText(ingredientName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`\\bmedi[oa]\\s+${name}\\b`).test(clean)) return { qty: 0.5, unit: 'pz', estimated: false };
    const normalizedLine = normalizeIngredientName(text);
    const normalizedIngredient = normalizeIngredientName(ingredientName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const piece = new RegExp(`\\b(\\d+(?:\\.\\d+)?|un|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\\s+${normalizedIngredient}\\b`).exec(normalizedLine);
    if (piece) return { qty: Number(piece[1]) || NUMBER_WORDS[piece[1]], unit: 'pz', estimated: true };
  }
  return { estimated: true, issue: 'Falta una cantidad y unidad medibles.' };
}

export interface RecipeProposalLine {
  ingredientId?: string;
  rawText: string;
  ingredientName: string;
  qty?: number;
  unit?: Unit;
  wastePct?: number;
  quantityBasis?: 'gross' | 'net' | 'unknown';
  isSubrecipe?: boolean;
  estimated: { qty?: boolean; unit?: boolean; wastePct?: boolean };
}

export interface RecipeProposal {
  recipeId?: string;
  sellingPriceGross?: number;
  name: string;
  group?: string;
  servings?: number;
  yieldQty?: number;
  yieldUnit?: Unit;
  isSubrecipe?: boolean;
  lines: RecipeProposalLine[];
  steps?: string[];
  notes?: string[];
}

export interface PlannedRecipeLine extends RecipeProposalLine {
  match: IngredientMatch;
  issue?: string;
  conversionNote?: string;
}

export interface RecipeProposalPlan {
  recipe: RecipeProposal;
  lines: PlannedRecipeLine[];
  newIngredients: string[];
  issues: string[];
}

interface PieceEstimate {
  aliases: string[];
  grams: number;
  wastePct: number;
}

// Promedios culinarios conservadores. Solo se aplican cuando el usuario habla
// en piezas y el ingrediente enlazado se compra por peso; siempre se muestran
// como estimación editable antes de guardar.
const PIECE_ESTIMATES: PieceEstimate[] = [
  { aliases: ['cebolla'], grams: 180, wastePct: 12 },
  { aliases: ['patata', 'papa'], grams: 180, wastePct: 15 },
  { aliases: ['tomate'], grams: 150, wastePct: 3 },
  { aliases: ['zanahoria'], grams: 100, wastePct: 12 },
  { aliases: ['pimiento'], grams: 180, wastePct: 18 },
  { aliases: ['calabacin'], grams: 250, wastePct: 5 },
  { aliases: ['berenjena'], grams: 300, wastePct: 8 },
  { aliases: ['limon'], grams: 120, wastePct: 45 },
  { aliases: ['naranja'], grams: 180, wastePct: 35 },
  { aliases: ['manzana'], grams: 180, wastePct: 12 },
  { aliases: ['pera'], grams: 170, wastePct: 12 },
  { aliases: ['aguacate'], grams: 200, wastePct: 30 },
  { aliases: ['diente ajo', 'ajo'], grams: 5, wastePct: 15 },
];

function pieceEstimateFor(...names: string[]): PieceEstimate | undefined {
  // No aplicar el promedio de una pieza a tamaños o formatos diferentes.
  if (/\b(cabezas?|grandes?|pequen[oa]s?|pelad[oa]s?|limpi[oa]s?)\b/.test(normalizeText(names.join(' ')))) return undefined;
  if (/\bajo\b/.test(normalizeText(names.join(' '))) && !/\bdientes?\b/.test(normalizeText(names.join(' ')))) return undefined;
  const normalized = names.map(normalizeIngredientName);
  return PIECE_ESTIMATES.find((estimate) => estimate.aliases.some((alias) =>
    normalized.some((name) => name === alias || name.startsWith(`${alias} `) || name.endsWith(` ${alias}`)),
  ));
}

/** Solo familias inequívocas; los demás productos se dejan para revisión. */
export function defaultRecipeWastePct(name: string): number | undefined {
  const normalized = normalizeIngredientName(name);
  if (/^(lechuga|espinaca|acelga)( |$)/.test(normalized)) return 35;
  if (/^(merluza|lubina|dorada|pescado) enter[oa]$/.test(normalized)) return 45;
  if (/^(pollo entero|carne con hueso)$/.test(normalized)) return 25;
  if (/^(arroz|harina|pasta|azucar|sal)( |$)/.test(normalized)) return 0;
  return undefined;
}

export function planRecipeProposal(
  recipe: RecipeProposal,
  catalog: RecipeProposalCatalogItem[],
): RecipeProposalPlan {
  const lines = recipe.lines.map((line) => {
    const parsed = line.qty != null && line.unit
      ? { qty: line.qty, unit: line.unit, estimated: false }
      : parseQuantity(line.rawText, line.ingredientName);
    const match = matchIngredient(line.ingredientName, catalog);
    const matchedIngredient = match.kind === 'exact' || match.kind === 'fuzzy'
      ? catalog.find((item) => item.ingredientId === match.candidates[0].ingredientId)
      : undefined;
    const qty = line.qty ?? parsed.qty;
    const unit = line.unit ?? parsed.unit;
    const pieceEstimate = qty != null
      && unit === 'pz'
      && matchedIngredient?.purchaseUnit
      && dimensionOf(matchedIngredient.purchaseUnit) === 'mass'
      ? pieceEstimateFor(line.ingredientName, matchedIngredient.name, line.rawText)
      : undefined;
    const convertedWaste = line.wastePct ?? pieceEstimate?.wastePct ?? defaultRecipeWastePct(line.ingredientName);
    const estimatedGrossGrams = pieceEstimate ? (qty ?? 0) * pieceEstimate.grams : undefined;
    const convertedQty = pieceEstimate && estimatedGrossGrams != null
      ? Math.round(estimatedGrossGrams * (1 - (convertedWaste ?? 0) / 100) * 10) / 10
      : line.quantityBasis === 'gross' && qty != null && convertedWaste != null
        ? qty * (1 - convertedWaste / 100)
        : qty;
    return {
      ...line,
      qty: convertedQty,
      quantityBasis: pieceEstimate || (line.quantityBasis === 'gross' && convertedWaste != null) ? 'net' as const : line.quantityBasis,
      unit: pieceEstimate ? 'g' as const : unit,
      wastePct: convertedWaste,
      estimated: {
        ...line.estimated,
        qty: pieceEstimate ? true : (line.estimated.qty ?? parsed.estimated),
        unit: pieceEstimate || (unit && ['cucharada', 'cucharadita', 'taza'].includes(unit)) ? true : line.estimated.unit,
        wastePct: convertedWaste != null && convertedWaste > 0 && line.wastePct == null ? true : line.estimated.wastePct,
      },
      match,
      issue: parsed.issue ?? (line.quantityBasis === 'gross' && convertedWaste == null
        ? '¿Cuánto pesa después de limpiar, o qué porcentaje descartas?'
        : unit === 'pz' && matchedIngredient?.purchaseUnit && dimensionOf(matchedIngredient.purchaseUnit) === 'mass' && !pieceEstimate
          ? '¿Cuánto pesa esta cantidad? No hay una equivalencia confirmada.' : undefined),
      conversionNote: pieceEstimate
        ? `${qty} ${qty === 1 ? 'pieza' : 'piezas'} ≈ ${estimatedGrossGrams} g brutos → ~${convertedQty} g netos · merma orientativa ${convertedWaste}%`
        : line.quantityBasis === 'gross' && convertedWaste != null
          ? `${qty} ${unit} de compra → ${convertedQty} ${unit} limpios · descarte ${convertedWaste}%`
          : (line as PlannedRecipeLine).conversionNote,
    };
  });
  return {
    recipe,
    lines,
    newIngredients: [...new Set(lines.filter((line) => line.match.kind === 'none').map((line) => line.ingredientName))],
    issues: lines.flatMap((line) => [
      ...(line.issue ? [`${line.ingredientName}: ${line.issue}`] : []),
      ...(line.match.kind === 'ambiguous' ? [`${line.ingredientName}: elige un ingrediente.`] : []),
    ]),
  };
}

export interface ProposalImportRows {
  recipeRows: Array<Omit<Recipe, 'tenantId' | 'recipeId' | 'updatedAt' | 'components'> & {
    components: Array<Omit<Recipe['components'][number], 'ref'> & {
      ref: Recipe['components'][number]['ref'] | { kind: 'ingredient_del_fichero'; ingredientName: string };
    }>;
  }>;
  ingredientStubs: IngredientInput[];
  issues: Array<{ line: number; message: string; candidates?: IngredientCandidate[] }>;
}

/** Dudas que impiden confirmar sin abrir la revisión. No convierte supuestos en hechos. */
export function recipeReviewQuestions(proposal: RecipeProposal, catalog: RecipeProposalCatalogItem[]): string[] {
  const plan = planRecipeProposal(proposal, catalog);
  return [
    ...(!proposal.servings && !proposal.isSubrecipe ? ['¿Para cuántas raciones es esta elaboración?'] : []),
    ...(proposal.isSubrecipe && (!proposal.yieldQty || !proposal.yieldUnit) ? ['¿Cuánto producto terminado obtienes?'] : []),
    ...(!proposal.lines.length ? ['¿Qué ingredientes y cantidades utilizas?'] : []),
    ...(proposal.notes?.length ? ['Revisa los detalles de la elaboración antes de guardar: ' + proposal.notes.join(' · ')] : []),
    ...plan.lines.flatMap((line) => line.issue ? [`${line.ingredientName}: ${line.issue}`]
      : line.qty == null || !line.unit ? [`¿Qué cantidad de ${line.ingredientName} utilizas?`]
      : Object.values(line.estimated).some(Boolean) ? [`Revisa la cantidad y la merma estimadas de ${line.ingredientName}.`] : []),
  ];
}

/** Plan puro: no escribe ni sustituye una coincidencia ambigua por un alta. */
export function proposalToImportRows(
  proposal: RecipeProposal,
  catalog: RecipeProposalCatalogItem[],
  recipes: Array<Pick<Recipe, 'recipeId' | 'name'>> = [],
  selections: Record<number, { ingredientId?: string; recipeId?: string; createNew?: boolean }> = {},
): ProposalImportRows {
  const plan = planRecipeProposal(proposal, catalog);
  const issues: ProposalImportRows['issues'] = [];
  const stubs = new Map<string, IngredientInput>();
  const components: ProposalImportRows['recipeRows'][number]['components'] = [];
  for (const [index, line] of plan.lines.entries()) {
    if (line.issue) {
      issues.push({ line: index, message: `${line.ingredientName}: ${line.issue}` });
      continue;
    }
    if (line.qty == null || !Number.isFinite(line.qty) || line.qty <= 0 || !line.unit ||
        (line.wastePct != null && (!Number.isFinite(line.wastePct) || line.wastePct < 0 || line.wastePct >= 100))) {
      issues.push({ line: index, message: line.issue ?? `Revisa cantidad, unidad y merma de «${line.ingredientName}».` });
      continue;
    }
    let ref: typeof components[number]['ref'];
    const selection = selections[index];
    if (selection?.ingredientId) {
      if (!catalog.some((ing) => ing.ingredientId === selection.ingredientId)) {
        issues.push({ line: index, message: 'El ingrediente elegido no está en tu catálogo.' });
        continue;
      }
      ref = { kind: 'ingredient', ingredientId: selection.ingredientId };
    } else if (selection?.recipeId) {
      if (!recipes.some((r) => r.recipeId === selection.recipeId)) {
        issues.push({ line: index, message: 'La subreceta elegida no está en tu catálogo.' });
        continue;
      }
      ref = { kind: 'recipe', recipeId: selection.recipeId };
    } else if (line.isSubrecipe) {
      const matches = recipes.filter((r) => normalizeIngredientName(r.name) === normalizeIngredientName(line.ingredientName));
      if (matches.length !== 1) {
        issues.push({ line: index, message: `Elige la subreceta «${line.ingredientName}».` });
        continue;
      }
      ref = { kind: 'recipe', recipeId: matches[0].recipeId };
    } else if (line.match.kind === 'ambiguous' && !selection?.createNew) {
      issues.push({ line: index, message: `Elige el ingrediente «${line.ingredientName}».`, candidates: line.match.candidates });
      continue;
    } else if (line.match.kind === 'none' || (selection?.createNew && line.match.kind !== 'exact')) {
      const key = normalizeIngredientName(line.ingredientName);
      const purchaseUnit = dimensionOf(line.unit) === 'mass' ? 'kg' : dimensionOf(line.unit) === 'volume' ? 'l' : 'pz';
      const previous = stubs.get(key);
      if (previous && previous.purchaseUnit !== purchaseUnit) {
        issues.push({ line: index, message: `«${line.ingredientName}» usa unidades incompatibles; indica su formato de compra.` });
        continue;
      }
      if (!previous) stubs.set(key, {
        name: line.ingredientName, purchaseQty: 1, purchaseUnit,
        group: 'otros', vatPct: 0, pricePending: true, createdVia: 'alexia', active: true,
      });
      ref = { kind: 'ingredient_del_fichero', ingredientName: previous?.name ?? line.ingredientName };
    } else {
      ref = { kind: 'ingredient', ingredientId: line.match.candidates[0].ingredientId };
    }
    components.push({ ref, block: 'ingrediente', qty: line.qty, unit: line.unit,
      wastePct: line.wastePct ?? 0,
      estimated: Object.values(line.estimated).some(Boolean) || undefined,
    });
  }
  // No devolver media receta lista para guardar cuando quedan dudas.
  return {
    ingredientStubs: [...stubs.values()], issues,
    recipeRows: issues.length ? [] : [{
      name: proposal.name, group: proposal.group, kind: proposal.isSubrecipe ? 'subreceta' : 'plato',
      portions: proposal.servings ?? 1, yieldQty: proposal.yieldQty ?? proposal.servings ?? 1,
      yieldUnit: proposal.yieldUnit ?? 'pz', useYieldCostAsBase: proposal.isSubrecipe ?? false,
      safetyMarginPct: 10, steps: proposal.steps?.join('\n'), components, active: true, createdVia: 'alexia',
    }],
  };
}
